/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { AccountTree, ArrowRight } from "@mui/icons-material";
import { BuilderQuestion, readableIdentity } from "./form-builder-utils";
import {
  getQuestionIdentityKey,
  getQuestionTypeIcon,
  QuestionGroupsBySection,
} from "./form-builder-shared";
import { guessQuestionTypeByName } from "./question-type-specs";

type Props = {
  questions: BuilderQuestion[];
  questionGroupsBySection: QuestionGroupsBySection;
  existingQuestions: Record<string, string>;
};

type QuestionMap = Record<string, BuilderQuestion[]>;

const buildQuestionMap = (questions: BuilderQuestion[]): QuestionMap =>
  questions.reduce<QuestionMap>((acc, question) => {
    const key = getQuestionIdentityKey(question);
    if (!acc[key]) acc[key] = [];
    acc[key].push(question);
    return acc;
  }, {});

const getRootQuestions = (questions: BuilderQuestion[]): BuilderQuestion[] => {
  if (questions.length === 0) return [];

  const questionKeys = new Set(questions.map((question) => getQuestionIdentityKey(question)));
  const childKeys = questions.reduce((acc, question) => {
    question.childrenKeys.forEach((childKey) => {
      if (questionKeys.has(childKey)) acc.add(childKey);
    });
    return acc;
  }, new Set<string>());

  const roots = questions.filter((question) => !childKeys.has(getQuestionIdentityKey(question)));
  return roots.length > 0 ? roots : questions;
};

type QuestionBranchParams = {
  question: BuilderQuestion;
  depth: number;
  pathKeys: Set<string>;
  questionsByIdentity: QuestionMap;
  existingQuestions: Record<string, string>;
};

const renderQuestionBranch = ({
  question,
  depth,
  pathKeys,
  questionsByIdentity,
  existingQuestions,
}: QuestionBranchParams): React.ReactNode => {
  const questionKey = getQuestionIdentityKey(question);
  const questionType = existingQuestions[question.name] || guessQuestionTypeByName(question.name) || "unknown";
  const questionLabel = question.title || readableIdentity(questionKey);

  const children = Array.from(
    new Map(
      question.childrenKeys
        .flatMap((childKey) => questionsByIdentity[childKey] || [])
        .map((childQuestion) => [childQuestion.draftId, childQuestion]),
    ).values(),
  );

  return (
    <Box key={question.draftId} sx={{ pl: depth * 2, py: 0.5 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
        {depth > 0 && <ArrowRight fontSize="small" color="disabled" />}
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", color: "text.secondary" }}>
          {getQuestionTypeIcon(questionType)}
        </Box>
        <Typography variant="body2" fontWeight={600}>{questionLabel}</Typography>
        {question.required && (
          <Typography component="span" color="primary.main" fontWeight={700}>
            *
          </Typography>
        )}
        {question.childrenKeys.length > 0 && <Chip size="small" label={`children: ${question.childrenKeys.length}`} />}
        <Typography variant="caption" color="text.secondary">{readableIdentity(questionKey)}</Typography>
      </Stack>

      {children.map((childQuestion) => {
        const childKey = getQuestionIdentityKey(childQuestion);
        if (pathKeys.has(childKey)) {
          return (
            <Box key={`cycle-${question.draftId}-${childQuestion.draftId}`} sx={{ pl: (depth + 1) * 2, py: 0.5 }}>
              <Typography variant="caption" color="warning.main">
                Cycle detected: {readableIdentity(childKey)}
              </Typography>
            </Box>
          );
        }

        return renderQuestionBranch({
          question: childQuestion,
          depth: depth + 1,
          pathKeys: new Set([...pathKeys, childKey]),
          questionsByIdentity,
          existingQuestions,
        });
      })}
    </Box>
  );
};

type QuestionGroupCardProps = {
  title: string;
  subtitle?: string;
  questions: BuilderQuestion[];
  questionsByIdentity: QuestionMap;
  existingQuestions: Record<string, string>;
};

const QuestionGroupCard: React.FC<QuestionGroupCardProps> = ({
  title,
  subtitle,
  questions,
  questionsByIdentity,
  existingQuestions,
}) => {
  const rootQuestions = getRootQuestions(questions);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          )}
        </Stack>

        <Box sx={{ mt: 1 }}>
          {questions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No questions in this group.
            </Typography>
          )}
          {rootQuestions.map((question) => renderQuestionBranch({
            question,
            depth: 0,
            pathKeys: new Set([getQuestionIdentityKey(question)]),
            questionsByIdentity,
            existingQuestions,
          }))}
        </Box>
      </CardContent>
    </Card>
  );
};

const FormBuilderTreePanel: React.FC<Props> = ({
  questions,
  questionGroupsBySection,
  existingQuestions,
}) => {
  const questionsByIdentity = React.useMemo(() => buildQuestionMap(questions), [questions]);

  return (
    <Stack spacing={2} sx={{ mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AccountTree fontSize="small" />
        <Typography variant="h6">Tree View</Typography>
      </Stack>

      {questionGroupsBySection.sectionGroups.map(({ section, questions: sectionQuestions }) => (
        <QuestionGroupCard
          key={section.draftId}
          title={`${section.title || section.name || "(untitled section)"} (${sectionQuestions.length})`}
          subtitle={section.description || ""}
          questions={sectionQuestions}
          questionsByIdentity={questionsByIdentity}
          existingQuestions={existingQuestions}
        />
      ))}

      <QuestionGroupCard
        title={`Unassigned (${questionGroupsBySection.unassignedQuestions.length})`}
        questions={questionGroupsBySection.unassignedQuestions}
        questionsByIdentity={questionsByIdentity}
        existingQuestions={existingQuestions}
      />
    </Stack>
  );
};

export default FormBuilderTreePanel;
